/**
 * Release 3 REV-1 — the inbox panel under the top-bar bell: newest first, a kind filter, mark one / mark all read,
 * click → the asset or board the notification is about. Live rows arrive through `lib/ws/apply.ts` section A.
 */
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCheck, Inbox } from 'lucide-react';
import { useNotifications, useMarkNotificationsRead, type Notification } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { Button } from '@/components/Button';
import { Select } from '@/components/Input';
import { Avatar, EmptyState, Spinner } from '@/components/Misc';
import { cx, formatRelative } from '@/lib/format';
import { KIND_FILTERS, notificationExcerpt, notificationLine, notificationTarget } from './notificationText';

export function NotificationsPanel({ onClose }: { onClose: () => void }) {
  const ws = useSessionStore((s) => s.workspace);
  const wsId = ws?.id;
  const [filter, setFilter] = useState('all');
  const kinds = useMemo(() => KIND_FILTERS.find((f) => f.id === filter)?.kinds ?? [], [filter]);
  const q = useNotifications(wsId, kinds.length ? { kinds } : {});
  const mark = useMarkNotificationsRead(wsId ?? '');
  const navigate = useNavigate();
  const location = useLocation();
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const unread = q.data?.pages[0]?.unread_count ?? 0;

  const open = (n: Notification) => {
    if (!n.read_at) mark.mutate({ ids: [n.id] });
    const target = notificationTarget(n);
    onClose();
    if (!ws || !target) return;
    if (target.startsWith('/a/')) navigate(`/w/${ws.slug}${target}`, { state: { background: location.pathname + location.search } });
    else navigate(`/w/${ws.slug}${target}`);
  };

  return (
    <div role="dialog" aria-label="Notifications" data-notifications-panel className="flex max-h-[70vh] w-[22rem] flex-col overflow-hidden rounded-lg border border-line bg-bg-elev shadow-xl sm:w-[26rem]">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2">
        <h2 className="text-sm font-semibold">Notifications</h2>
        {unread > 0 && <span className="rounded-full bg-accent px-1.5 text-[11px] font-semibold text-white" data-unread-count>{unread}</span>}
        <Select aria-label="Filter notifications" value={filter} onChange={(e) => setFilter(e.target.value)} className="ml-auto h-7 w-auto text-xs">
          {KIND_FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </Select>
        <Button size="sm" variant="ghost" onClick={() => mark.mutate({ all: true })} disabled={unread === 0 || mark.isPending} title="Mark all as read" data-mark-all-read>
          <CheckCheck size={13} /> <span className="hidden sm:inline">Mark all read</span>
        </Button>
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto" aria-label="Notification list">
        {q.isPending && <li className="flex justify-center py-8 text-fg-muted"><Spinner /></li>}
        {!q.isPending && items.length === 0 && <li><EmptyState icon={<Inbox size={24} />} title={filter === 'all' ? 'You are all caught up' : 'Nothing here'} body={filter === 'all' ? 'Mentions, replies and activity on boards you follow show up here.' : undefined} /></li>}
        {items.map((n) => (
          <li key={n.id} data-notification={n.id} data-unread={!n.read_at || undefined} className={cx('border-b border-line last:border-b-0', !n.read_at && 'bg-accent-soft/40')}>
            <button type="button" className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-bg-sunken" onClick={() => open(n)}>
              <Avatar actor={n.actor ?? null} size={26} className="mt-0.5 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className={cx('block text-sm leading-snug', !n.read_at && 'font-medium')}>{notificationLine(n)}</span>
                {notificationExcerpt(n) && <span className="mt-0.5 block truncate text-xs text-fg-muted">“{notificationExcerpt(n)}”</span>}
                <span className="mt-0.5 block text-[11px] text-fg-subtle">{formatRelative(n.created_at)}</span>
              </span>
              {!n.read_at && <span aria-label="Unread" className="mt-2 h-2 w-2 shrink-0 rounded-full bg-accent" />}
            </button>
          </li>
        ))}
        {q.hasNextPage && (
          <li className="p-2 text-center">
            <Button size="sm" variant="ghost" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>{q.isFetchingNextPage ? 'Loading…' : 'Load more'}</Button>
          </li>
        )}
      </ul>
    </div>
  );
}
